import moment from "moment-timezone"

import { activityType, ActivityTypeKeyReverse } from "@/constants/activity"
import { Country } from "@/types/account"
import { Session } from "@/types/session"
import { Activity, ActivityDetail } from "@/types/activity"
import { DATE_FORMAT, REPORT_DATE_FORMAT } from "@/constants/date"
import { toCamelCase } from "./string"

export function convertGroupAge(groupAge: string) {
  // 연령정보 컨버팅
  return groupAge === "A" ? "Adult" : groupAge
}

export function convertGender(gender: "F" | "M") {
  // 성별 정보 컨버팅
  return {
    F: "Female",
    M: "Male",
  }[gender]
}

export function globalTime(date: string) {
  return moment(date).format("HH:mm")
}
export function globalDate(date: string, isReport?: boolean) {
  return moment(date).format(isReport ? REPORT_DATE_FORMAT : DATE_FORMAT)
}

export function getSecondsToMMSS(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = Math.floor(totalSeconds % 60)

  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
}

export const getSingleItemDuration = (item: Session | ActivityDetail | Activity) => {
  const diff = moment(item.dateFinish).diff(moment(item.dateStart))

  return getDurationString(diff / 1000 / 60)
}

export const getWholeItemDuration = (itemList: (Session | ActivityDetail | Activity)[]) => {
  const diff = itemList.reduce(
    (acc, item) => acc + moment(item.dateFinish).diff(moment(item.dateStart)),
    0,
  )

  return getDurationString(diff / 1000 / 60)
}

export const getWholeDuration = (target: string, item: ActivityDetail) => {
  switch (target) {
    case "activity": {
      return getSingleItemDuration(item)
    }
    case "session":
      return getWholeItemDuration(item.sessions)

    default:
      return undefined
  }
}
/**
 * 분(m)를 시간('mm' 'ss')으로 변환
 * @param valueMin number
 * @returns string
 */
export function getDurationString(valueMin: number) {
  const valueSec = Math.floor(valueMin * 60)
  const minutes = Math.floor(valueSec / 60)
  const seconds = valueSec - minutes * 60

  return `${minutes}’ ${seconds}”`
}

export function convertCountryName(country: string, countryList: Country[]) {
  // 국가 정보 code 기준으로 name 컨버팅
  return countryList ? countryList.find((item) => item.code === country)?.name : country
}

export function setSerialNumberFormat(serialNumber: number) {
  const stringSerialNum = serialNumber.toString()
  const formats: Record<number, string> = {
    1: "0000",
    2: "000",
    3: "00",
    4: "0",
    5: "",
  }

  return `${formats[stringSerialNum.length]}${stringSerialNum}`
}

export function periodFormat(period: "D" | "W" | "M") {
  return {
    D: "daily",
    W: "weekly",
    M: "monthly",
  }[period]
}

export function convertActivityCode(type?: string) {
  const values = Object.values(activityType) as string[]

  if (type && values.includes(type)) {
    return Object.entries(activityType).reduce(
      (acc: Record<string, keyof typeof activityType>, cur) => {
        acc[cur[1]] = cur[0]

        return acc
      },
      {} as Record<keyof ActivityTypeKeyReverse, string>,
    )[type as keyof typeof activityType]
  }

  return ""
}

export function convertSerial(serialNumber: string) {
  const [model, version, number] = serialNumber.split("-")

  return { model: model || "", version: version || "", number: number || 0 }
}

// runEffort1, maxRunEffort1, runEffort2, maxRunEffort2 같은 문자열을 번역을 위해 분리하는 함수
export function splitByEffortPattern(str: string) {
  // PascalCase를 단어 단위로 분리
  const tokens = str.match(/[A-Z]?[a-z]+[0-9]*/g)
  if (!tokens) {
    return [str]
  }

  // effort + number 패턴을 찾기
  const effortIndex = tokens.findIndex((token) => /\d/.test(token))
  if (effortIndex === -1) {
    return tokens.map((t) => t.toLowerCase())
  }

  const beforeEffort = tokens.slice(0, effortIndex - 1)
  const effortPart = tokens[effortIndex - 1] + tokens[effortIndex]
  const afterEffort = tokens.slice(effortIndex + 1)

  return [
    ...(beforeEffort.length ? beforeEffort.map(toCamelCase) : [undefined]),
    toCamelCase(effortPart),
    ...afterEffort.map(toCamelCase),
  ]
}

/**
 * 현재 타임존 오프셋을 GMT 형식으로 반환
 * @returns string (예: "GMT+09", "GMT-05")
 */
export function getTimezoneOffset(): string {
  const offset = moment().utcOffset()
  return "GMT" + (offset / -60 <= 0 ? "+" : "-") + ("0" + Math.abs(offset / -60)).slice(-2)
}
